import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Flex, Text, VStack } from '@chakra-ui/react'
import { InfoWindow, useMap } from '@vis.gl/react-google-maps'
import { LuRefreshCw, LuMapPin } from 'react-icons/lu'
import { useNavigate } from 'react-router-dom'
import { programacionesService } from '../../../api/services'
import type { VerificationMapItemResponse } from '../../../api/models/response/programacion'
import { KMap, FitBounds } from '../../../lib/googleMaps'
import { useT } from '../../../i18n'

const STATUS_COLORS: Record<number, string> = {
  1: '#2b5285',
  2: '#d49a2a',
  3: '#1d6664',
}

type LL = google.maps.LatLngLiteral

function todayIsoDate(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

// Polígono imperativo sobre google.maps.Polygon (la base no exporta uno).
// Mismo ciclo de vida que lib/googleMaps/Polyline.tsx: crear con setMap(map),
// setOptions en cambios, cleanup con setMap(null).
function Polygon({
  paths,
  color,
  fillOpacity = 0.25,
  weight = 2,
  onClick,
}: {
  paths: LL[]
  color: string
  fillOpacity?: number
  weight?: number
  onClick?: () => void
}) {
  const map = useMap()
  const ref = useRef<google.maps.Polygon | null>(null)

  useEffect(() => {
    if (!map) return
    const poly = new google.maps.Polygon()
    poly.setMap(map)
    ref.current = poly
    return () => {
      poly.setMap(null)
      ref.current = null
    }
  }, [map])

  useEffect(() => {
    ref.current?.setOptions({
      paths,
      strokeColor: color,
      strokeWeight: weight,
      strokeOpacity: 1,
      fillColor: color,
      fillOpacity,
      clickable: !!onClick,
    })
  }, [paths, color, fillOpacity, weight, onClick])

  useEffect(() => {
    const poly = ref.current
    if (!poly || !onClick) return
    const listener = poly.addListener('click', onClick)
    return () => listener.remove()
  }, [onClick])

  return null
}

function formatDateShort(s: string): string {
  if (!s) return '-'
  try {
    return new Date(s + 'T00:00:00').toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch { return s }
}

function formatDateTime(dt: string | null): string {
  if (!dt) return '-'
  try {
    return new Date(dt).toLocaleString('es-PE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch { return dt }
}

interface PolygonItem {
  item: VerificationMapItemResponse
  paths: LL[]
  center: LL
  color: string
}

// Centroide simple (promedio de vértices) para anclar el InfoWindow.
function centroid(paths: LL[]): LL {
  let lat = 0
  let lng = 0
  for (const p of paths) {
    lat += p.lat
    lng += p.lng
  }
  return { lat: lat / paths.length, lng: lng / paths.length }
}

export default function MapaVerificacionesPage() {
  const { t } = useT()
  const navigate = useNavigate()
  const [items, setItems] = useState<VerificationMapItemResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await programacionesService.getMap(todayIsoDate())
      setItems(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void fetchData() }, [fetchData])

  // Parseo de boundaryGeoJson → paths {lat,lng}. GeoJSON usa [lng,lat]; Google {lat,lng}.
  const polygonItems = useMemo<PolygonItem[]>(() => {
    const out: PolygonItem[] = []
    for (const it of items) {
      if (!it.boundaryGeoJson) continue
      try {
        const g = JSON.parse(it.boundaryGeoJson)
        const ring = g.coordinates[0] as number[][]
        const paths: LL[] = ring.map((c) => ({ lat: c[1], lng: c[0] }))
        if (paths.length === 0) continue
        out.push({
          item: it,
          paths,
          center: centroid(paths),
          color: STATUS_COLORS[it.statusGroup] ?? '#857b71',
        })
      } catch { /* ignore */ }
    }
    return out
  }, [items])

  const fitPoints = useMemo<LL[]>(() => polygonItems.flatMap((p) => p.paths), [polygonItems])
  const boundsNonce = fitPoints.length

  return (
    <Box position="relative" h="calc(100vh - 120px)" display="flex" flexDirection="column" gap={4}>
      <Flex alignItems="center" justifyContent="space-between" flexShrink={0}>
        <Box>
          <Text fontSize="2xl" fontWeight="800">{t('inteligencia.verificaciones.title')}</Text>
          <Text fontSize="sm" color="#857b71">{t('inteligencia.verificaciones.subtitle')}</Text>
        </Box>
        <Flex
          as="button"
          alignItems="center"
          gap={2}
          px={4}
          py={2}
          borderRadius="md"
          bg="#b4532a"
          color="white"
          fontSize="sm"
          fontWeight="600"
          cursor={loading ? 'wait' : 'pointer'}
          opacity={loading ? 0.7 : 1}
          pointerEvents={loading ? 'none' : 'auto'}
          _hover={{ opacity: loading ? 0.7 : 0.9 }}
          onClick={fetchData}
        >
          <Box as="span" display="inline-flex" style={{ animation: loading ? 'spin 1s linear infinite' : undefined }}>
            <LuRefreshCw size={16} />
          </Box>
          {t('inteligencia.verificaciones.refresh')}
        </Flex>
      </Flex>

      {error && (
        <Box bg="#fae1db" color="#991b1b" p={3} borderRadius="md" fontSize="sm">{error}</Box>
      )}

      <Box flex={1} position="relative" borderRadius="xl" overflow="hidden" border="1px solid #e8dfd1">
        <KMap
          defaultCenter={{ lat: -12, lng: -76 }}
          defaultZoom={6}
          mapTypeId="roadmap"
          mapTypeControl
        >
          <FitBounds points={fitPoints} padding={30} nonce={boundsNonce} />

          {polygonItems.map(({ item, paths, color }) => (
            <Polygon
              key={item.detailId}
              paths={paths}
              color={color}
              fillOpacity={0.25}
              weight={2}
              onClick={() => setOpenId(item.detailId)}
            />
          ))}

          {polygonItems.map(({ item, center }) =>
            openId === item.detailId ? (
              <InfoWindow key={`info-${item.detailId}`} position={center} onCloseClick={() => setOpenId(null)}>
                <Box minW="280px" p={1}>
                  {/* Minero (full width) */}
                  <Box mb={2.5}>
                    <Text fontSize="10px" color="#857b71" textTransform="uppercase" fontWeight="700" letterSpacing="0.04em">{t('inteligencia.verificaciones.popup.miner')}</Text>
                    <Text fontSize="sm" fontWeight="700" lineHeight="1.3">{item.companyName}</Text>
                  </Box>

                  {/* DM + Programación (2 col) */}
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2.5}>
                    <Box>
                      <Text fontSize="10px" color="#857b71" textTransform="uppercase" fontWeight="700" letterSpacing="0.04em">{t('inteligencia.verificaciones.popup.dm')}</Text>
                      <Text fontSize="sm" lineHeight="1.3">{item.miningRightName}</Text>
                      {item.miningRightCode && <Text fontSize="11px" color="#857b71" fontFamily="monospace">{item.miningRightCode}</Text>}
                    </Box>
                    <Box>
                      <Text fontSize="10px" color="#857b71" textTransform="uppercase" fontWeight="700" letterSpacing="0.04em">{t('inteligencia.verificaciones.popup.schedule')}</Text>
                      <Text fontSize="sm" lineHeight="1.3">{item.scheduleCode ?? item.scheduleName ?? '-'}</Text>
                    </Box>
                  </Box>

                  {/* Fecha Inicio + Fecha Fin (2 col) */}
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2.5}>
                    <Box>
                      <Text fontSize="10px" color="#857b71" textTransform="uppercase" fontWeight="700" letterSpacing="0.04em">{t('inteligencia.verificaciones.popup.scheduleStart')}</Text>
                      <Text fontSize="sm" lineHeight="1.3">{formatDateShort(item.scheduleStartDate)}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="10px" color="#857b71" textTransform="uppercase" fontWeight="700" letterSpacing="0.04em">{t('inteligencia.verificaciones.popup.scheduleEnd')}</Text>
                      <Text fontSize="sm" lineHeight="1.3">{formatDateShort(item.scheduleEndDate)}</Text>
                    </Box>
                  </Box>

                  {/* Geólogo(s) (full width) */}
                  {item.geologists.length > 0 && (
                    <Box mb={2.5}>
                      <Text fontSize="10px" color="#857b71" textTransform="uppercase" fontWeight="700" letterSpacing="0.04em">{t('inteligencia.verificaciones.popup.geologist')}</Text>
                      {item.geologists.map((g, i) => (
                        <Text key={i} fontSize="sm" lineHeight="1.3">
                          {g.fullName}
                          {g.colegiatureCode && <Text as="span" color="#857b71" fontSize="11px"> — CIP {g.colegiatureCode}</Text>}
                        </Text>
                      ))}
                    </Box>
                  )}

                  {/* Inicio + Finalizado timestamps (2 col if ambos) */}
                  {(item.startedAt || item.completedAt) && (
                    <Box display="grid" gridTemplateColumns={item.startedAt && item.completedAt ? '1fr 1fr' : '1fr'} gap={2} mb={2.5}>
                      {item.startedAt && (
                        <Box>
                          <Text fontSize="10px" color="#857b71" textTransform="uppercase" fontWeight="700" letterSpacing="0.04em">{t('inteligencia.verificaciones.popup.startedAt')}</Text>
                          <Text fontSize="sm" lineHeight="1.3">{formatDateTime(item.startedAt)}</Text>
                        </Box>
                      )}
                      {item.completedAt && (
                        <Box>
                          <Text fontSize="10px" color="#857b71" textTransform="uppercase" fontWeight="700" letterSpacing="0.04em">{t('inteligencia.verificaciones.popup.completedAt')}</Text>
                          <Text fontSize="sm" lineHeight="1.3">{formatDateTime(item.completedAt)}</Text>
                        </Box>
                      )}
                    </Box>
                  )}

                  {item.statusGroup >= 2 && (
                    <Flex
                      as="button"
                      alignItems="center"
                      justifyContent="center"
                      w="100%"
                      px={3}
                      py={2}
                      borderRadius="md"
                      bg="#b4532a"
                      color="white"
                      fontSize="xs"
                      fontWeight="700"
                      cursor="pointer"
                      onClick={() => navigate(`/app/programaciones/${item.scheduleId}/details/${item.detailId}/visita`)}
                    >
                      {t('inteligencia.verificaciones.popup.viewVisit')}
                    </Flex>
                  )}
                </Box>
              </InfoWindow>
            ) : null,
          )}
        </KMap>

        <Box
          position="absolute"
          bottom={4}
          right={4}
          bg="white"
          borderRadius="md"
          p={3}
          boxShadow="0 2px 8px rgba(0,0,0,0.15)"
          zIndex={1000}
        >
          <VStack alignItems="flex-start" gap={1.5}>
            {[1, 2, 3].map((g) => (
              <Flex key={g} alignItems="center" gap={2}>
                <Box w="14px" h="14px" borderRadius="sm" bg={STATUS_COLORS[g]} opacity={0.9} />
                <Text fontSize="xs" fontWeight="600">
                  {g === 1 && t('inteligencia.verificaciones.legend.scheduled')}
                  {g === 2 && t('inteligencia.verificaciones.legend.inProgress')}
                  {g === 3 && t('inteligencia.verificaciones.legend.completedToday')}
                </Text>
              </Flex>
            ))}
          </VStack>
        </Box>

        {!loading && polygonItems.length === 0 && (
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            bg="white"
            px={6}
            py={4}
            borderRadius="md"
            boxShadow="0 2px 8px rgba(0,0,0,0.15)"
            zIndex={1000}
          >
            <Flex alignItems="center" gap={2}>
              <LuMapPin size={16} color="#857b71" />
              <Text fontSize="sm" color="#857b71">{t('inteligencia.verificaciones.empty')}</Text>
            </Flex>
          </Box>
        )}
      </Box>
    </Box>
  )
}
